import {parseNumber} from '@benchling/js-utils';
import moment from 'moment';

import {assert} from 'Asserts';
import {JSONValue} from 'util/Types';

import BenchlingTypeDeserializationException from './BenchlingTypeDeserializationException';

type DeserializationResult<DeserializedType> =
  | {
      deserialized: false;
      value: JSONValue;
    }
  | {
      deserialized: true;
      value: DeserializedType;
    };

type Deserializer<DeserializedType> = (value: JSONValue) => DeserializationResult<DeserializedType>;

/**
 * Helper to deserialize a value with type safety, throwing an exception if the value cannot be deserialized.
 * @param serializedValue The value to deserialize
 * @param deserializer The deserializer function
 * @throws BenchlingTypeDeserializationException If the value cannot be deserialized
 */
function deserializeOrThrow<DeserializedType>(
  serializedValue: JSONValue,
  deserializer: Deserializer<DeserializedType>
): DeserializedType {
  const deserializationResult = deserializer(serializedValue);
  if (deserializationResult.deserialized) {
    return deserializationResult.value;
  }
  throw new BenchlingTypeDeserializationException(`Could not deserialize ${serializedValue}`);
}

/**
 * Deserialize a serialized Datetime value.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 */
export const tryDeserializeDatetime: Deserializer<Date> = (value) =>
  typeof value === 'string' && moment(value, moment.ISO_8601).isValid()
    ? {deserialized: true, value: moment.utc(value).toDate()}
    : {deserialized: false, value};

/**
 * Deserialize a serialized Date value.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 */
export const tryDeserializeDate: Deserializer<Date> = (value) =>
  typeof value === 'string' && moment(value, moment.ISO_8601).isValid()
    ? // Calling moment directly assumes local timezone, which is ok since dates can't specify timezones
      {deserialized: true, value: moment(value).toDate()}
    : {deserialized: false, value};

function convertInf(value: string): number {
  if (value === 'inf') {
    return Number.POSITIVE_INFINITY;
  } else if (value === '-inf') {
    return Number.NEGATIVE_INFINITY;
  }
  assert(false, "Expected function to only be called on 'inf' or '-inf' value.", {value});
}

/**
 * Deserialize a serialized Decimal value to a JS Number.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 */
export const tryDeserializeDecimalToFloat: Deserializer<number> = (value) => {
  const numericValue =
    typeof value === 'string' ? (value.includes('inf') ? convertInf(value) : parseNumber(value)) : null;
  return numericValue == null ? {deserialized: false, value} : {deserialized: true, value: numericValue};
};

/**
 * Deserialize a serialized Datetime value, throwing an exception if the value cannot be deserialized.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 * @throws BenchlingTypeDeserializationException if the value cannot be deserialized
 */
export function deserializeDatetime(value: JSONValue): Date {
  return deserializeOrThrow(value, tryDeserializeDatetime);
}

/**
 * Deserialize a serialized Date value, throwing an exception if the value cannot be deserialized.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 * @throws BenchlingTypeDeserializationException if the value cannot be deserialized
 */
export function deserializeDate(value: JSONValue): Date {
  return deserializeOrThrow(value, tryDeserializeDate);
}

/**
 * Deserialize a serialized Decimal value to a JS Number, throwing an exception if the value cannot be deserialized.
 * See `leaf_type_validators[_test].py` for the canonical serialization format.
 * @throws BenchlingTypeDeserializationException if the value cannot be deserialized
 */
export function deserializeDecimalToFloat(value: JSONValue): number {
  return deserializeOrThrow(value, tryDeserializeDecimalToFloat);
}
